/* eslint-disable no-unused-vars*/
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Col, Form, Input, Row, Switch, Skeleton, Select, Tag } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { postCreateCategoryTutorial, getCategoryTutorialById, putUpdateCategoryTutorial } from 'api/categoryTutorial';
import { getCookie } from 'helper/cookie';
import Swal from 'sweetalert2';
import { get } from 'lodash';
import { messageRequireIcon } from 'common/messageError/message.common';
import { dataColor } from 'constants/tagColor';
import { getRolesAll } from 'api/roles';

interface propType {
  id: number;
}

const CreateTutorialCategory = (props: propType) => {
  const { id } = props;
  const isMobileView = useSelector(state => get(state, 'device.isMobileView', false));
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const token = getCookie('access_tokenltl');
  const [data, setData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [checkDefault, setCheckDefault] = useState(Boolean);
  const [dataOptions, setDataOptions] = useState([{}]);
  const [dataRoles, setDataRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const fetchCategoryById = useCallback(
    async (query: any) => {
      setIsLoading(true);
      const params = {
        id: id,
      };
      try {
        const responseData = await getCategoryTutorialById({ token, params: params });
        if (responseData?.status === 200) {
          const { data } = responseData;
          let splitData = null;
          if (data?.data?.category_tutorial_view_type) {
            splitData = data?.data?.category_tutorial_view_type.split(',');
          }
          setData(data?.data);
          setIsLoading(false);
          form.setFieldsValue({
            category_tutorial_name: data?.data?.category_tutorial_name,
            category_tutorial_slug: data?.data?.category_tutorial_slug,
            category_tutorial_status: data?.data?.category_tutorial_status === 'Y' ? true : false,
            category_tutorial_view_type: splitData,
          });
          setCheckDefault(data?.data?.category_tutorial_status === 'Y' ? true : false);
        } else {
          setData('');
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
      }
    },
    [token, id],
  );

  useMemo(() => {
    if (id && token) {
      fetchCategoryById(id);
    }
  }, [id]);

  const fetchRolesList = useCallback(
    async (query?: any) => {
      setIsLoadingRoles(false);
      const params = {};
      try {
        const responseData = await getRolesAll({ token, params: params });
        if (responseData?.status === 200) {
          const { data } = responseData;

          setDataRoles(data?.data?.roles);
          renderOptionView(data?.data?.roles);
          setIsLoadingRoles(false);
        } else {
          setDataRoles([]);
          setIsLoadingRoles(false);
        }
      } catch (error) {
        setIsLoadingRoles(false);
      }
    },
    [token, id],
  );

  const renderOptionView = (data: any) => {
    const options: { value: string; label: string }[] = [];
    return data.map((value: any) => {
      options.push({
        value: value.roles_name,
        label: value.roles_name,
      });
      setDataOptions(options);
    });
  };

  const tagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: any) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const filterColor = dataColor.filter(f => f.text === label)[0];
    return (
      <Tag
        color={filterColor?.color}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{
          marginRight: 3,
          display: 'flex',
        }}
      >
        {label}
      </Tag>
    );
  };

  useMemo(() => {
    if (token) {
      fetchRolesList();
    }
  }, [token]);

  const handleSubmit = async (values: any) => {
    const params = {
      category_tutorial_name: values?.category_tutorial_name,
      category_tutorial_slug: values?.category_tutorial_slug,
      category_tutorial_status: values.category_tutorial_status ? 'Y' : 'N',
      category_tutorial_view_type: values.category_tutorial_view_type,
    };
    if (id) {
      const updateData = await putUpdateCategoryTutorial({ token, params: params, id: id });
      if (updateData.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'อัปเดตข้อมูลสำเร็จ',
          showConfirmButton: false,
          timer: 5000,
        });
        navigate(`/backoffice/category_tutorial`, { replace: true });
        // await fetchCategoryById(id);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'อัปเดตข้อมูลไม่สำเร็จ',
          showConfirmButton: false,
          timer: 5000,
        });
      }
    } else {
      const createData = await postCreateCategoryTutorial({ token, params: params });
      if (createData.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'บันทึกข้อมูลสำเร็จ',
          showConfirmButton: false,
          timer: 5000,
        });
        // await fetchCategoryById(id);
        navigate(`/backoffice/category_tutorial`, { replace: true });
        // navigate(`/backoffice/category_tutorial/edit/${createData?.data?.data?.id}`, { replace: true });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'บันทึกข้อมูลไม่สำเร็จ',
          showConfirmButton: false,
          timer: 5000,
        });
      }
    }
  };
  return (
    <>
      {isLoading === false ? (
        <Form
          layout="vertical"
          autoComplete="off"
          onFinish={handleSubmit}
          form={form}
          initialValues={{
            category_tutorial_name: '',
            category_tutorial_status: 'Y',
          }}
        >
          <Row gutter={16} justify="space-between">
            <Col span={isMobileView ? 24 : 12}>
              <Form.Item
                name="category_tutorial_slug"
                label="รหัสหมวดหมู่วีดีโอ"
                rules={[
                  {
                    required: true,
                    message: messageRequireIcon('กรุณากรอกข้อมูล รหัสหมวดหมู่วีดีโอ'),
                  },
                ]}
              >
                <Input type="text" placeholder="รหัสหมวดหมู่วีดีโอ" />
              </Form.Item>
            </Col>
            <Col span={isMobileView ? 24 : 12}>
              <Form.Item
                name="category_tutorial_name"
                label="ชื่อหมวดหมู่วีดีโอ"
                rules={[
                  {
                    required: true,
                    message: messageRequireIcon('กรุณากรอกข้อมูล ชื่อหมวดหมู่วีดีโอ'),
                  },
                ]}
              >
                <Input type="text" placeholder="ชื่อหมวดหมู่วีดีโอ" />
              </Form.Item>
            </Col>

            <Col span={isMobileView ? 24 : 12}>
              <Form.Item
                name="category_tutorial_view_type"
                label="ระดับการมองเห็น"
                rules={[
                  {
                    required: false,
                    message: messageRequireIcon('กรุณากรอกข้อมูล ระดับการมองเห็น'),
                  },
                ]}
              >
                <Select
                  placeholder="ระดับการมองเห็น"
                  mode="multiple"
                  // showArrow
                  tagRender={tagRender}
                  //  defaultValue={['gold', 'cyan']}
                  style={{
                    width: '100%',
                  }}
                  options={dataOptions}
                />
              </Form.Item>
            </Col>

            <Col span={isMobileView ? 24 : 12}>
              <Form.Item
                name="category_tutorial_status"
                label="สถานะการใช้งาน"
                rules={[
                  {
                    required: false,
                    message: messageRequireIcon('กรุณากรอกข้อมูล สถานะการใช้งาน'),
                  },
                ]}
                initialValue={checkDefault}
              >
                <Switch checkedChildren={'ใช้งาน'} unCheckedChildren={'ไม่ใช้งาน'} defaultChecked={!id ? true : checkDefault} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              <Link to="/backoffice/category_tutorial">
                <Button type="text" htmlType="button" size="large" className="btn btn-md btn-secondary">
                  <ArrowLeftOutlined /> ย้อนกลับ
                </Button>
              </Link>
            </Col>
            <Col span={12}>
              <Button type="primary" htmlType="submit" size="large" className="btn btn-md" style={{ float: 'right' }}>
                <SaveOutlined /> {id ? 'อัปเดตข้อมูล' : `เพิ่มข้อมูล`}
              </Button>
            </Col>
          </Row>
        </Form>
      ) : (
        <Skeleton active />
      )}
    </>
  );
};

export default CreateTutorialCategory;
